import { useState } from 'react';
import { useSelector } from 'react-redux';
import { SectionDivider, SectionHeader } from '../styles';
import styled from 'styled-components';
import caretUp from '../../../icons/caret_up.svg';
import caretRight from '../../../icons/caret_right.svg';
import useGetProhibitedOperations from '../../../hooks/useGetProhibitedOperations';
import useGetIndustry from '../../../hooks/useGetIndustry';
import BusinessSummaryDrawer from './BusinessSummaryDrawer';
import { useMediaQuery } from 'react-responsive';
import { selectPolicyTypes } from '../../../selectors/application';
import { PolicyType } from '../../../types/enums';

type BusinessSummaryProps = {
  application: Application;
};

export default function BusinessSummary({ application }: BusinessSummaryProps) {
  const { legalBusinessName, dba, contactFirstName, contactLastName, akHash, industryName } = application;
  const [isIneligibleOpen, setIsIneligibleOpen] = useState(true);
  const { data: prohibitedOperations } = useGetProhibitedOperations(akHash);
  const { data: fetchedIndustryName } = useGetIndustry(!industryName ? akHash : undefined);
  const [open, setOpenChange] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const displayIndustryName = industryName || fetchedIndustryName;
  const policyTypes = useSelector(selectPolicyTypes);
  const isPlOnly = policyTypes.length === 1 && policyTypes.includes(PolicyType.PL);
  const hasProhibitedOperations = prohibitedOperations && prohibitedOperations.length > 0;

  function toggleIneligibleActivities(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsIneligibleOpen(!isIneligibleOpen);
  }

  return (
    <div>
      {isMobile && <SectionDivider />}
      <SectionHeader>Your Business</SectionHeader>
      <BusinessSummaryContainer>
        <BusinessSummaryInnerContainer>
          <BusinessSection id="info">
            <h3>Business Name</h3>
            {legalBusinessName && <p data-cy="summary-business-name">{legalBusinessName}</p>}
            {dba && <p>{dba}</p>}
          </BusinessSection>
          {displayIndustryName && (
            <BusinessSection id="industry">
              <h3>Industry</h3>
              <p>{displayIndustryName}</p>
              {hasProhibitedOperations && !isPlOnly && (
                <IneligibleActivitiesDetails open={isIneligibleOpen}>
                  <IneligibleActivitiesSummary onClick={toggleIneligibleActivities}>
                    <span>Ineligible Activities and Exposures</span>
                    <CaretUp src={caretUp} alt="Caret Up" $open={isIneligibleOpen} />
                  </IneligibleActivitiesSummary>
                  <section>
                    <p className="description">
                      The specific activities listed below would make this business ineligible for a quote.
                    </p>
                    <IneligibleListsContainer>
                      <ul>
                        {prohibitedOperations.map((operation: string, index: number) => (
                          <li key={index}>{operation}</li>
                        ))}
                      </ul>
                    </IneligibleListsContainer>
                    <p className="note">
                      If your business performs any of these activities, do not proceed with this quote.
                    </p>
                  </section>
                </IneligibleActivitiesDetails>
              )}
            </BusinessSection>
          )}
          <BusinessSection id="contact">
            <h3>Primary Contact</h3>
            <p>{`${contactFirstName} ${contactLastName}`}</p>
          </BusinessSection>
        </BusinessSummaryInnerContainer>
        <ViewDetailsButton onClick={() => setOpenChange(!open)}>
          See all business details <CaretRight src={caretRight} alt="Caret Right" height={16} width={10} />
        </ViewDetailsButton>
      </BusinessSummaryContainer>
      <BusinessSummaryDrawer application={application} open={open} onOpenChange={setOpenChange} />
      <SectionDivider />
    </div>
  );
}

const BusinessSummaryContainer = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #d4d4dd;
  background: ${({ theme }) => theme.colors.neutral.special};
  margin-top: 8px;
`;

const BusinessSummaryInnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BusinessSection = styled.div`
  background: ${({ theme }) => theme.colors.neutral.white};
  display: flex;
  padding: 8px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;

  h3 {
    color: ${({ theme }) => theme.colors.tertiary.zero};
    font-family: ${({ theme }) => theme.font.typeface.primary};
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 21px;
    margin: 0;
  }

  p {
    color: ${({ theme }) => theme.colors.neutral.dark};
    font-family: ${({ theme }) => theme.font.typeface.secondary};
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }

  .description {
    color: ${({ theme }) => theme.colors.tertiary.zero};
    font-family: ${({ theme }) => theme.font.typeface.secondary};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  .note {
    color: ${({ theme }) => theme.colors.tertiary.zero};
    font-family: ${({ theme }) => theme.font.typeface.secondary};
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
    padding-bottom: 16px;
  }
`;

const IneligibleListsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin: 16px 0;

  ul {
    flex: 1;
    margin: 0;
    padding-left: 16px;
    columns: 2;
    list-style: disc;

    @media (max-width: 768px) {
      columns: 1;
    }
  }

  li {
    color: ${({ theme }) => theme.colors.tertiary.zero};
    font-family: ${({ theme }) => theme.font.typeface.secondary};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }
`;

const IneligibleActivitiesDetails = styled.details``;
const IneligibleActivitiesSummary = styled.summary`
  color: ${({ theme }) => theme.colors.primary.zero};
  font-family: ${({ theme }) => theme.font.typeface.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  display: flex;
  padding: 8px 16px 8px 0;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const CaretUp = styled.img<{ $open: boolean }>`
  display: flex;
  height: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  transform: ${({ $open }) => ($open ? 'rotate(0deg)' : 'rotate(180deg)')};
  transition: transform 0.2s ease;
`;

const CaretRight = styled.img``;

const ViewDetailsButton = styled.button`
  margin-top: 24px;
  display: flex;
  height: 32px;
  padding: 8px 14px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.tertiary.zero};
  text-align: center;
  font-family: ${({ theme }) => theme.font.typeface.primary};
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  border-radius: 100px;
  border: 1px solid ${({ theme }) => theme.colors.neutral.regular};
  background: none;
  cursor: pointer;

  &:focus,
  &:focus-visible {
    outline: none !important;
  }
`;
